'use client';

import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import type { CampaignType, MarketingValues } from './campaign';
import MarketingScripts from './MarketingScripts';

export type { CampaignType, MarketingValues } from './campaign';

/**
 * `marketing_attrs` is a JSON column the pages hand over with an `as CampaignType` cast, and
 * `[[...slug]]/page.tsx` fills `internal_lead_type_id` from `page.claim_section_id`, which is
 * nullable — so a field typed `number` really can arrive `null` or missing. The guards below
 * are load-bearing, not defensive noise, and the parameter is typed `Partial` to say so.
 */
function toMarketingValues(campaign: Partial<CampaignType>, offerId: number): MarketingValues {
  return {
    campaignId: campaign?.campaign?.id ?? 0,
    offerId,
    leadTypeId: campaign?.lead_type?.id ?? 0,
    leadTypeName: campaign?.lead_type?.name ?? '',
    leadTypeInternalId: campaign?.internal_lead_type_id ?? 0,
    leadScoreBaseline: campaign?.lead_score_baseline ?? 0,
    leadSourceId: campaign?.lead_source?.id ?? 0,
    leadSourceName: campaign?.lead_source?.name ?? '',
    leadSourceTypeId: campaign?.lead_source?.type?.id ?? 0,
    leadSourceTypeName: campaign?.lead_source?.type?.name ?? '',
  };
}

/**
 * What a surface with no campaign attribution resolves to. The page components render in three
 * places and only one of them is a campaign page: the public site (wrapped in a provider), the
 * admin editor canvas, and the not-found views. The last two have nothing to attribute — and
 * must not boot the marketing scripts — so they read this instead, which is exactly what the
 * consumers used to fall back to when the bootstrap had not run.
 *
 * Derived from `toMarketingValues` rather than spelled out, so the defaults live in one place
 * and a new field cannot be added to one and forgotten in the other. Frozen because it is the
 * default context value: a stray mutation would corrupt every one of those surfaces at once.
 */
export const NO_MARKETING: MarketingValues = Object.freeze(toMarketingValues({}, 0));

const MarketingContext = createContext<MarketingValues>(NO_MARKETING);

/**
 * A campaign can override the offer the page was built with through `?oid=`. Anything that is
 * not a positive number is ignored — the offer id is sent to the lead endpoint as a number, and
 * used to flow through as a raw string, which reached `document.cookie` unescaped.
 */
function readOfferIdOverride(): number | null {
  const raw = new URLSearchParams(window.location.search).get('oid');
  if (!raw) return null;

  const offerId = Number(raw);

  return Number.isFinite(offerId) && offerId > 0 ? offerId : null;
}

// The url is a browser-only value and these pages are statically generated, so it is read
// through a store rather than during render: `undefined` is what the server and the hydration
// render see, and the real override arrives on the first client render after that. Both
// snapshots are primitives, so React can compare them without a cache.
//
// `popstate` covers a back/forward move between two urls of the same route — the one case where
// the query string changes under a provider that stays mounted. A `pushState` navigation, which
// is what the App Router does, delivers a new `campaign` prop and re-renders the segment, so the
// store is read again there anyway.
const subscribeToUrl = (onStoreChange: () => void) => {
  window.addEventListener('popstate', onStoreChange);

  return () => window.removeEventListener('popstate', onStoreChange);
};
const getServerOfferIdOverride = (): number | null | undefined => undefined;
const getOfferIdOverride = (): number | null | undefined => readOfferIdOverride();

export function MarketingProvider({
  campaign,
  children,
}: {
  campaign: CampaignType;
  children: ReactNode;
}) {
  // `undefined` until the override has been read, which is what holds the scripts back: they
  // must be handed the effective offer id, and they only ever boot once.
  const offerIdOverride = useSyncExternalStore(
    subscribeToUrl,
    getOfferIdOverride,
    getServerOfferIdOverride
  );

  // Memoised on the *effective* offer id, never on the override itself. Hydration reads the
  // override as `undefined` and the render right after it as `null` on the pages with no
  // `?oid=` — which is nearly all of them — and keying the snapshot on that transition would
  // hand every consumer a new object, re-rendering every form on the page for nothing. Keyed
  // on the number, those two renders produce the identical snapshot and the context never
  // propagates.
  const effectiveOfferId = offerIdOverride ?? campaign?.offer?.id ?? 0;

  const marketing = useMemo(
    () => toMarketingValues(campaign, effectiveOfferId),
    [campaign, effectiveOfferId]
  );

  return (
    <MarketingContext.Provider value={marketing}>
      {children}
      {offerIdOverride !== undefined && <MarketingScripts marketing={marketing} />}
    </MarketingContext.Provider>
  );
}

export function useMarketing(): MarketingValues {
  return useContext(MarketingContext);
}
